import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useProcessing } from "@/context/ProcessingContext";
import { UploadCloud, X, ImagePlus, Sparkles, Info } from "lucide-react";

const MIN = 6;

export default function NewProduct() {
  const navigate = useNavigate();
  const { start, stop } = useProcessing();
  const inputRef = useRef(null);
  const [photos, setPhotos] = useState([]); // {file, url, id}
  const [submitting, setSubmitting] = useState(false);
  const [drag, setDrag] = useState(false);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    const mapped = incoming.map((f) => ({ file: f, url: URL.createObjectURL(f), id: `${f.name}-${f.size}-${Math.random()}` }));
    setPhotos((prev) => [...prev, ...mapped]);
  };

  const remove = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    addFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (photos.length < MIN) return toast.error(`Envie no mínimo ${MIN} fotos.`);
    setSubmitting(true);
    start("Recebendo suas fotos e preparando tudo...");
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("files", p.file));
      const { data } = await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/products/${data.id}/generate`);
      toast.success("Solange começou a montar seu anúncio!");
      navigate(`/produto/${data.id}`);
    } catch (err) {
      stop();
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#9c5f27]">Novo produto</h1>
        <p className="mt-2 text-[#5c4634]">Quanto mais ângulos, melhor o anúncio. Precisamos de pelo menos <b>{MIN} fotos</b>.</p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="photo-upload-zone"
          className={`mt-6 rounded-3xl border-2 border-dashed p-10 text-center cursor-pointer transition-all ${
            drag ? "border-[#f0791f] bg-[#fff2e6]" : "border-[#e8d8c8] bg-white hover:border-[#c17f3e]"
          }`}
        >
          <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="photo-input" />
          <div className="w-16 h-16 rounded-2xl bg-[#f0791f] flex items-center justify-center text-white mx-auto">
            <UploadCloud size={30} />
          </div>
          <p className="mt-4 font-display text-lg font-semibold text-[#2e1e10]">Arraste as fotos aqui ou clique para escolher</p>
          <p className="mt-1 text-sm text-[#8c735f]">JPG, PNG ou WEBP</p>
        </div>

        {/* Contador */}
        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className={`font-semibold ${photos.length >= MIN ? "text-[#15803d]" : "text-[#b45309]"}`} data-testid="photo-count">
              {photos.length} / {MIN} fotos
            </span>
            {photos.length < MIN && <span className="text-[#8c735f]">— faltam {MIN - photos.length}</span>}
          </div>
          <button onClick={() => inputRef.current?.click()} className="text-sm font-medium text-[#f0791f] flex items-center gap-1" data-testid="add-more-photos">
            <ImagePlus size={16} /> Adicionar mais
          </button>
        </div>

        {photos.length > 0 && (
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="photo-previews">
            {photos.map((p, i) => (
              <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-[#e8d8c8] group">
                <img src={p.url} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">{i + 1}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                  className="absolute top-1 right-1 bg-white/90 text-[#b91c1c] rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`remove-photo-${i}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-[#fdf8f3] border border-[#e8d8c8] p-4 flex gap-3 text-sm text-[#5c4634]">
          <Info size={18} className="text-[#c17f3e] shrink-0 mt-0.5" />
          <span>A Solange não inventa código de barras (GTIN) nem dados fiscais (NCM). Esses campos ficam pra você preencher manualmente se precisar.</span>
        </div>

        <button
          onClick={submit}
          disabled={submitting || photos.length < MIN}
          className="btn-orange w-full mt-6 py-4 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="generate-listing-btn"
        >
          <Sparkles size={18} /> {submitting ? "Enviando..." : "Gerar anúncio com a Solange"}
        </button>
      </div>
    </div>
  );
}
